<template>
  <div data-aos="fade-up" data-aos-duration="500">
    <div
      class="transition-all duration-300 pointer-events-auto hover:shadow-[0_0_1.5rem_0_rgba(0,0,0,.12)] hover:-translate-y-1 cursor-pointer"
    >
      <div
        class="border-2 border-gray-300 bg-white rounded w-full h-[600px] text-center p-5 flex flex-col relative overflow-hidden"
        :class="{
          'border-primary': isSelected,
          'border-gray-300': !isSelected,
        }"
      >
        <!-- Modern corner badge for "Tercih Edilen" -->
        <div v-if="PromotedAsPopular" class="popular-badge">
          <span>Tercih Edilen</span>
        </div>

        <!-- Yearly discount badge -->
        <span
          v-if="isYearly"
          class="text-xs text-primary mt-2 text-primary/90 bg-primary/10 px-2 py-1 rounded-full inline-block absolute top-5 left-5 z-10 shadow-sm"
        >
          -%
          {{
            Math.floor(
              ((MonthlySellingPrice * 12) / YearlySellingPrice) * 100 - 100
            )
          }}
        </span>

        <!-- Header section -->
        <div class="mb-4">
          <!-- Package name with fixed height -->
          <div class="h-8 flex items-center justify-center">
            <span class="text-lg text-primary">{{ Name }}</span>
          </div>

          <!-- switch for yearly or monthly -->
          <div class="flex justify-center mt-4">
            <label class="switch">
              <input type="checkbox" v-model="isYearly" />
              <span class="slider round"></span>
            </label>
            <span class="ml-2">{{ isYearly ? "Yıllık" : "Aylık" }}</span>
          </div>

          <h1 class="text-3xl/tight font-semibold mt-3">
            {{
              isYearly
                ? Math.floor(YearlySellingPrice / 12)
                : MonthlySellingPrice
            }}
            <sup class="text-gray-500 text-sm font-normal"> TL </sup>
            <sub class="text-gray-500 text-sm font-normal">/ Ay</sub>
          </h1>
        </div>

        <!-- Description section with truly fixed height -->
        <div class="border-b border-gray-200 w-full mb-4">
          <div class="h-[100px] overflow-y-auto py-2 description-container">
            <p class="text-gray-600">
              {{ Description }}
            </p>
          </div>
        </div>

        <!-- Features section -->
        <div class="flex-grow flex flex-col justify-between">
          <div class="flex flex-col gap-4 overflow-y-auto">
            <p
              class="flex items-center text-gray-600 gap-4"
              v-for="(PackageFeature, index) in PackageFeatures"
              :key="index"
              @mouseover="showTooltip(index)"
              @mouseleave="hideTooltip"
            >
              <i class="fa-solid fa-check text-green-500 text-lg"></i>
              {{ PackageFeature.FeatureHeadline }}
              <span v-if="activeTooltip === index" class="tooltip">
                {{ PackageFeature.FeatureDescription }}
              </span>
            </p>

            <p class="flex items-start text-gray-600 gap-4" v-if="isYearly">
              * Yıllık olarak faturalandırılır. Yıllık
              {{ YearlySellingPrice }} TL
            </p>
            <p class="flex items-start text-gray-600 gap-4" v-else>
              * Aylık olarak faturalandırılır. Yıllık
              {{ MonthlySellingPrice * 12 }} TL
            </p>
          </div>

          <!-- Button at the bottom -->
          <div class="mt-4 mb-1">
            <a
              @click="handleButtonClick"
              class="bg-primary/10 text-primary/90 w-full py-3 rounded-lg border border-transparent hover:border hover:border-primary/20 transition-all duration-300 inline-block"
              >{{ props.buttonText }}</a
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { toRefs, ref, watch, defineEmits } from "vue";
import { defineProps } from "vue";
import { useRoute, useRouter } from "vue-router";

const isYearly = ref(true);
const activeTooltip = ref(null);
const route = useRoute();
const router = useRouter();
const showTooltip = (index) => {
  activeTooltip.value = index;
};

const emit = defineEmits(["onYearlyChange"]);

// watch isYearly and emit event
watch(isYearly, (newVal) => {
  emit("onYearlyChange", newVal);
});

const hideTooltip = () => {
  activeTooltip.value = null;
};

const props = defineProps({
  packagex: {
    type: Object,
    required: true,
  },
  buttonText: {
    type: String,
    default: "Hemen Başla",
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
});

const handleButtonClick = () => {
  // if route is home, go for register
  if (route.name === "home") {
    router.push({ name: "signup" });
  }
};

const {
  Name,
  YearlySellingPrice,
  Description,
  MonthlySellingPrice,
  PackageFeatures,
  PromotedAsPopular,
} = toRefs(props.packagex);
</script>

<style scoped>
.tooltip {
  position: fixed;
  padding: 5px;
  left: 10;
  transform: translateY(-10px);

  background-color: #333;
  color: rgb(255, 255, 255);
  border-radius: 4px;
  white-space: nowrap;
  font-size: 0.875rem;
  z-index: 1;
  opacity: 1;
  transition: opacity 0.2s ease;
}

/* Tooltip görünürken */
.tooltip-enter-active,
.tooltip-leave-active {
  opacity: 1;
} /* Switch container */
.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
  margin-right: 5px;
}

/* Hide the checkbox */
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

/* Slider */
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 34px;
}

/* Rounded slider handle */
.slider.round:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 4px;
  bottom: 2px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

/* When checked */
input:checked + .slider {
  background-color: #4caf50; /* Toggle color when active */
}

input:checked + .slider:before {
  transform: translateX(26px); /* Move handle to the right */
}

/* Add fixed height to description area */
.description-container {
  min-height: 100px;
  max-height: 100px;
}

/* Kaldırılacak ribbon stili */
.ribbon-container,
.ribbon,
.ribbon:before {
  display: none; /* Eski stilleri kaldırmak için */
}

/* Yeni köşe etiketi */
.popular-badge {
  position: absolute;
  top: 0;
  right: 0;

  background-color: var(--color-primary, #4caf50);
  color: white;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 8px 16px;
  z-index: 10;
  border-bottom-left-radius: 8px;
  box-shadow: -2px 2px 8px rgba(0, 0, 0, 0.1);
  clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
}

.popular-badge span {
  display: block;
  transform: translateY(-1px);
  margin-bottom: -5px;
}

/* Container'a overflow hidden ekleyerek badge'in köşeye tam oturmasını sağladık */
</style>
